import useWindowEventHandlers from "hooks/useEventDispatcher";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import ChitRequest from "pages/ChitRequest/ChitRequest";
import TimeRecordingHelper from "./MyMatters/TimeRecordingHelper";
import MatterForm from "pages/Matters/MatterForm";
import AddClients from "pages/Clients/AddViewClients";

import { getClientData, getMatterAllInfo } from "api";
import {
  clearMatterBalances,
  setMatterAllInfo,
} from "services/mattersService";

import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import clientStyles from "../pages/Clients/Clients.module.scss";

const EventListenerComponent = () => {
    const { registerHandler, registerMessageHandler } = useWindowEventHandlers();

    const [showMatterForm, setShowMatterForm] = useState(false);
    const [selectedMatter, setSelectedMatter] = useState(null);
    const [clientData, setClientData] = useState({});
    const [showClientForm, setShowClientForm] = useState(false);
    const [timeRecordingModal, setTimeRecordingModal] = useState(false);
    const [chitRequestModal, setChitRequestModal] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    /* ---------------- helpers ---------------- */

    const closeAllDialogs = useCallback(() => {
        setTimeRecordingModal(false);
        setChitRequestModal(false);
        setShowMatterForm(false);
        setShowClientForm(false);
        setSelectedMatter(null);
        setClientData({});
    }, []);

    const actionDispatcher = useCallback(async (action, matterId) => {
        try {
            const response = await getMatterAllInfo(matterId);
            setSelectedMatter(response.data.data);

            switch (action) {
                case "ViewMatter":
                    setShowMatterForm(true);
                    dispatch(setMatterAllInfo(response.data.data));
                    return;
                case "AddChit":
                    setChitRequestModal(true);
                    return;
                case "AddTime":
                    setTimeRecordingModal(true);
                    return;
                default:
                    return;
            }
        } catch {
            alert("No matter exists against this Id.");
            navigate("/matters");
        }
    }, [dispatch, navigate]);

    const clientsDataGetter = useCallback(async (clientId) => {
        try {
            const data = await getClientData(clientId);
            setClientData(data?.data?.data);
            setShowClientForm(true);
        } catch {
            alert("No client exists against this Id.");
        }
    }, []);

    /* ---------------- registrations ---------------- */

    useEffect(() => {
        const unsubscribers = [];

        const handlers = {
            OpenDashboard: () => {
                closeAllDialogs();
                navigate("/dashboard");
            },
            OpenClients: () => {
                closeAllDialogs();
                navigate("/clients");
            },
            OpenMatters: () => {
                closeAllDialogs();
                navigate("/matters");
            },
            OpenOnboarding: () => {
                closeAllDialogs();
                navigate("/clients/onboarding");
            },
            OpenCasefile: (matterId) => {
                try {
                    window.chrome?.webview?.postMessage(`CasefileOpened:${matterId}`);
                } catch { }
                closeAllDialogs();
                navigate(`/matters/${matterId}`);
            },
            ViewMatter: (matterId) => {
                closeAllDialogs();
                actionDispatcher("ViewMatter", matterId);
            },
            AddChit: (matterId) => {
                closeAllDialogs();
                actionDispatcher("AddChit", matterId);
            },
            AddTime: (matterId) => {
                closeAllDialogs();
                actionDispatcher("AddTime", matterId);
            },
            ViewClient: (clientId) => {
                closeAllDialogs();
                clientsDataGetter(clientId);
            },
        };

        // Register all handlers and collect unsubscribe functions
        Object.entries(handlers).forEach(([name, handler]) => {
            unsubscribers.push(registerHandler(name, handler));
            unsubscribers.push(registerMessageHandler(name, handler));
        });

        // Cleanup on unmount or dependency change
        return () => {
            unsubscribers.forEach((unsubscribe) => unsubscribe?.());
        };

    }, [registerHandler, registerMessageHandler, navigate, closeAllDialogs, actionDispatcher, clientsDataGetter]);

    /* ---------------- UI ---------------- */

    return (
        <>
            {chitRequestModal && (
                <ChitRequest
                    matter={selectedMatter}
                    matterId={selectedMatter?.matterId}
                    component="modal"
                    viewMode="dialog"
                    cancelEdit={() => {
                        setChitRequestModal(false);
                        setSelectedMatter(null);
                        dispatch(clearMatterBalances("Chit Request"));
                    }}
                />
            )}

            {timeRecordingModal && (
                <TimeRecordingHelper
                    matter={selectedMatter}
                    cancelEdit={() => {
                        setTimeRecordingModal(false);
                        setSelectedMatter(null);
                        dispatch(clearMatterBalances("Time Recording"));
                    }}
                />
            )}

            {showMatterForm && (
                <MatterForm
                    isView={!!selectedMatter?.matterRef}
                    reference={selectedMatter?.matterRef}
                    setshowMatterForm={() => {
                        setShowMatterForm(false);
                        setSelectedMatter(null);
                    }}
                />
            )}

            {showClientForm && (
                <Dialog
                    title="Update Client"
                    onClose={() => setShowClientForm(false)}
                    className="dialog-xxl"
                >
                    <div className={`container ${clientStyles["clinet-dialog-top-div"]}`}>
                        <AddClients
                            data={clientData}
                            isView
                            formId="edit"
                            type="VIEW"
                            onClose={() => setShowClientForm(false)}
                        />
                    </div>
                    <div className="dialog-footer mt-2 px-0">
                        <Button
                            className="common-btn bgBtnSecondary border-0 me-2"
                            onClick={() => setShowClientForm(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="common-btn me-4"
                            onClick={() =>
                                document.getElementById("client-submitter")?.click()
                            }
                        >
                            Update Client
                        </Button>
                    </div>
                </Dialog>
            )}
        </>
    );
};

export default EventListenerComponent;
